// Modelo de pantalla de Segmentos: 0-N segmentos por cliente, sin prioridad. Un cliente entra si
// cumple al menos uno de los cohorts o está en la lista manual, que gana siempre. Estándar es el
// catch-all.
import type {
    SegmentationRecountRunResponse,
    SegmentationRecountStatus,
    SegmentationRecountTrigger,
    SegmentMembershipReason,
    SegmentStatusWire,
} from '@/api/services/loan-offerings/segments.contract';
import type { SegmentColorSlot } from '@/shared/patterns/Pills/segmentColors';

/** La paleta vive con la píldora que la pinta (`shared/patterns/Pills`); aquí solo se reexporta. */
export type { SegmentColorSlot } from '@/shared/patterns/Pills/segmentColors';
export { FALLBACK_COLOR_SLOT } from '@/shared/patterns/Pills/segmentColors';

export interface SegmentColorOption {
    slot: SegmentColorSlot;
    label: string;
    /** El punto del swatch, en el mismo HSL que el dot del chip. */
    swatch: string;
}

/** `COLOR_SLOTS` del mock, en su orden: el picker pinta 4 por renglón, dos renglones. */
export const SEGMENT_COLOR_OPTIONS: readonly SegmentColorOption[] = [
    { slot: 'slate', label: 'Gris', swatch: 'hsl(215 20% 50%)' },
    { slot: 'sky', label: 'Azul', swatch: 'hsl(199 80% 50%)' },
    { slot: 'amber', label: 'Ámbar', swatch: 'hsl(35 85% 52%)' },
    { slot: 'violet', label: 'Violeta', swatch: 'hsl(262 55% 60%)' },
    { slot: 'emerald', label: 'Verde', swatch: 'hsl(158 55% 45%)' },
    { slot: 'teal', label: 'Teal', swatch: 'hsl(174 55% 40%)' },
    { slot: 'pink', label: 'Rosa', swatch: 'hsl(325 60% 58%)' },
    { slot: 'rose', label: 'Rojo', swatch: 'hsl(0 65% 60%)' },
];

export type SegmentStatus = SegmentStatusWire;

/** Un segmento tal como lo lee la pantalla. */
export interface Segment {
    segmentId: string;
    name: string;
    description: string;
    colorSlot: SegmentColorSlot;
    /** Cohorts del segmento; entre ellos la relación es O. */
    cohortIds: string[];
    /** Lista manual por `retailCustomerId`: entran aunque no cumplan ningún cohort. */
    memberIds: string[];
    /** El catch-all. No acepta cohorts ni individuos y no se archiva. */
    isDefault: boolean;
    status: SegmentStatus;
    version: number;
    /** Clientes del último recálculo; `null` = sin calcular. En Estándar es el complemento. */
    clientCount: number | null;
    pctTotal: number | null;
    countedAt: string | null;
    countPending: boolean;
    createdBy: string;
    updatedBy: string;
    createdAt: string;
    updatedAt: string;
}

/** Lo que el operador está editando. Los campos que el PUT puede parchar, y nada más. */
export interface SegmentDraft {
    name: string;
    description: string;
    colorSlot: SegmentColorSlot;
    cohortIds: string[];
    memberIds: string[];
}

/** Qué segmentos lista la tabla. */
export type SegmentListFilter = SegmentStatus;

export const SEGMENT_FILTER_LABEL: Record<SegmentListFilter, string> = {
    ACTIVE: 'Activos',
    ARCHIVED: 'Archivados',
};

/** Las columnas por las que la lista ordena. Sin conteo, «Clientes» va al final. */
export type SegmentSortKey = 'name' | 'cohorts' | 'members' | 'customers';

export interface SegmentSort {
    key: SegmentSortKey;
    direction: 'asc' | 'desc';
}

export const DEFAULT_SEGMENT_SORT: SegmentSort = { key: 'name', direction: 'asc' };

/** `.head-btn` del mock. El `size="sm"` del kit mide 32px de alto y el mock pide 24. */
export const HEAD_BUTTON_CLASS = 'h-auto px-2.5 py-1 text-[11px]';

/** El cuerpo que el mock le da a `.search-x`: el buscador del toolbar y el de individuos. */
export const SEARCH_INPUT_CLASS = 'text-[11.5px] placeholder:text-[11.5px] focus:ring-0';

/** El de `.search-x.compact`: las dos cajas de la lista doble, un punto más chicas. */
export const COMPACT_SEARCH_INPUT_CLASS = 'text-[11px] placeholder:text-[11px] focus:ring-0';

/** El nombre con el que nace un segmento nuevo, como en el mock. */
export const NEW_SEGMENT_NAME = 'Nuevo segmento';

/** Tope del nombre, para que el operador no escriba de más y lo descubra al guardar. */
export const SEGMENT_NAME_MAX_LENGTH = 60;

/** Tope de la descripción. */
export const SEGMENT_DESCRIPTION_MAX_LENGTH = 240;

/** Tope del motivo, igual para segmentos y cohorts. */
export const REASON_MAX_LENGTH = 280;

/** Cuántos cohorts admite un segmento. */
export const MAX_SEGMENT_COHORTS = 20;

/** Qué cuenta la columna Clientes. */
export const CUSTOMER_COUNT_COLUMN_NOTE =
    'Clientes del último recálculo y su porcentaje sobre los acreditados del tenant.';

/** Dónde se aplica un segmento: políticas y promociones, con su estado. */
export interface SegmentUsageItem {
    id: string;
    name: string;
    status: string;
}

export interface SegmentUsage {
    policies: SegmentUsageItem[];
    promotions: SegmentUsageItem[];
}

/** El estado de una política o promoción en «Aplica en». Uno desconocido se muestra tal cual. */
export const USAGE_STATUS_LABEL: Readonly<Record<string, string>> = {
    DRAFT: 'Borrador',
    PUBLISHED: 'Publicada',
    PAUSED: 'Pausada',
    EXPIRED: 'Vencida',
    ARCHIVED: 'Eliminada',
};

/** Una corrida del recálculo de conteos. */
export type RecountRun = SegmentationRecountRunResponse;

export const RECOUNT_TRIGGER_LABEL: Record<SegmentationRecountTrigger, string> = {
    NIGHTLY: 'Nocturno',
    MANUAL: 'Manual',
};

export const RECOUNT_STATUS_LABEL: Record<SegmentationRecountStatus, string> = {
    RUNNING: 'En curso',
    FINISHED: 'Terminado',
    FAILED: 'Falló',
};

/** Cada cuánto se consultan las corridas mientras una sigue abierta. */
export const RECOUNT_POLL_MS = 5_000;

/** Cuánto se espera a que cierre la corrida lanzada antes de dejar de consultar. */
export const RECOUNT_GIVE_UP_MS = 30 * 60_000;

/** Un segmento al probar un cliente: si entra y por qué. */
export interface CustomerSegmentResult {
    segmentId: string;
    name: string;
    matched: boolean;
    reason: SegmentMembershipReason | null;
    matchedCohortIds: string[];
}

export interface CustomerSegmentsTest {
    retailCustomerId: string;
    /** Los segmentos activos que no son el catch-all. */
    segments: CustomerSegmentResult[];
    /** No cayó en ninguno: le aplica Estándar. */
    defaultApplies: boolean;
    /** `false` = los cohorts no se pudieron evaluar; solo cuenta la lista manual. */
    evaluatorAvailable: boolean;
}

/**
 * En qué estado le llega el catálogo de segmentos a una pantalla que lo consume prestado
 * (Promociones, Políticas). `ready` es el único que autoriza a mostrar nombres.
 */
export type SegmentCatalogState = 'ready' | 'loading' | 'forbidden' | 'unavailable';
